import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  DEFAULT_CURRENCY_CODE,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  TitleStrategy,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TimepickerConfig } from 'ngx-bootstrap/timepicker';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { provideMatomo } from 'ngx-matomo-client';
import { APP_ENVIRONMENT as EDGEDB_APP_ENVIRONMENT, BASE_PATH as EDGEDB_BASE_PATH } from '@bn/app-database/openapi';
import { MCApiV3 } from '@bn/app-magic-container';
import { BASE_PATH } from '@bn/app-mc//src/lib/openapi-logging';
import { BASE_PATH as SHIELD_BASE_PATH, SHIELD_LOGS_PATH } from '@bn/app-shield';
import { AuthHeaderInterceptor, ErrorInterceptor } from '@bn/auth/interceptors';
import { UserDetailsStateService } from '@bn/auth/services';
import { MENU_ACCESS } from '@bn/auth/tokens/menu-access.token';
import { AIGatewayApi, CoreApi, StreamVideoApi } from '@bn/core-api';
import { initApp } from '@bn/dashboard-shared/factory';
import { BnTemplatePageTitleStrategy } from '@bn/dashboard-shared/resolvers';
import { BASE_PATH as ORIGIN_LOGS_BASE_PATH } from '@bn/origin-logs-api';
import { BASE_PATH as PARTNER_PORTAL_BASE_PATH } from '@bn/partner-portal-api';
import { APP_CONFIG, ENV_CONFIG } from '@bn/shared/entities/injection-tokens';
import { HttpErrorInterceptor } from '@bn/shared/entities/interceptors';
import { AppConfig, EnvConfig } from '@bn/shared/entities/models';
import { VatPipe } from '@bn/shared/entities/pipes';
import { fontawesomeProviders } from '@bn/shared/font-awesome';
import { VersionCheckService } from '@bn/shared/services';
import { MixpanelEventsDirective } from '@bn/shared/utils/directives';
import { provideWebUi } from '@bn/web-ui';
import { EXT_URL_CONFIG, ExternalUrlConfig } from '@bn/web-ui/external-url';
import { environment } from '../environments/environment';
import { appRoutes } from './app-routing.module';
import { AppConfiguration, ExtUrlConfiguration } from './config';

export const appConfig: EnvironmentProviders = makeEnvironmentProviders([
  DecimalPipe,
  CurrencyPipe,
  VatPipe,
  // Injectable-only: root-provided so services (not just page components) can track events.
  MixpanelEventsDirective,
  provideTranslateService({
    loader: provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: '.json', enforceLoading: false }),
    fallbackLang: 'en',
    lang: 'en',
  }),
  provideZoneChangeDetection({ eventCoalescing: true }),
  { provide: AIGatewayApi.BASE_PATH, useValue: environment.aiGatewayApiUrl },
  { provide: CoreApi.BASE_PATH, useValue: environment.apiUrl },
  { provide: DEFAULT_CURRENCY_CODE, useFactory: (APP_CONFIG: AppConfig): string => APP_CONFIG.currencyCode, deps: [APP_CONFIG] },
  { provide: EDGEDB_BASE_PATH, useValue: environment.apiEdgeDbLiteUrl },
  { provide: EDGEDB_APP_ENVIRONMENT, useValue: environment },
  { provide: ORIGIN_LOGS_BASE_PATH, useValue: environment.cdnOriginErrorLogsUrl },
  { provide: PARTNER_PORTAL_BASE_PATH, useValue: environment.apiUrl },
  { provide: MCApiV3.BASE_PATH, useValue: environment.apiMcUrl },
  { provide: SHIELD_BASE_PATH, useValue: environment.apiUrl },
  { provide: SHIELD_LOGS_PATH, useValue: environment.shieldLogsUrl },
  { provide: StreamVideoApi.BASE_PATH, useValue: environment.streamApiUrl },
  {
    provide: StreamVideoApi.CONFIGURATION_PATH,
    useFactory: (basePath: string): StreamVideoApi.Configuration => new StreamVideoApi.Configuration({ basePath }),
    deps: [StreamVideoApi.BASE_PATH],
  },
  { provide: BASE_PATH, useValue: environment.mcLoggingUrl },
  { provide: APP_CONFIG, useFactory: (ENV_CONFIG: EnvConfig): AppConfig => new AppConfiguration(ENV_CONFIG).getConfig(), deps: [ENV_CONFIG] },
  { provide: ENV_CONFIG, useValue: environment },
  { provide: EXT_URL_CONFIG, useFactory: (ENV_CONFIG: EnvConfig): ExternalUrlConfig => new ExtUrlConfiguration(ENV_CONFIG).getUrls(), deps: [ENV_CONFIG] },
  { provide: MENU_ACCESS, useExisting: UserDetailsStateService },
  { provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
  { provide: TitleStrategy, useClass: BnTemplatePageTitleStrategy },
  // Statistics is the only timepicker usage: 24h, hours-only (no meridian, minutes or seconds).
  {
    provide: TimepickerConfig,
    useFactory: (): TimepickerConfig => Object.assign(new TimepickerConfig(), { showMeridian: false, showMinutes: false, showSeconds: false }),
  },
  provideAppInitializer(() => {
    const initializerFn = initApp(inject(VersionCheckService));
    return initializerFn();
  }),
  fontawesomeProviders.provideFontawesome(),
  provideWebUi(),
  provideMatomo({
    disabled: !environment.production,
    runOutsideAngularZone: true,
    siteId: 1,
    trackerUrl: 'https://matomo.bunny.net',
  }),
  provideHighlightOptions({
    coreLibraryLoader: () => import('highlight.js/lib/core'),
    languages: {
      typescript: () => import('highlight.js/lib/languages/typescript'),
    },
  }),
  provideRouter(
    appRoutes,
    withInMemoryScrolling({
      anchorScrolling: 'enabled',
      scrollPositionRestoration: 'disabled',
    }),
    // withDebugTracing(),
    // withViewTransitions({
    //   onViewTransitionCreated: ({ transition, to }) => {
    //     const router = inject(Router);
    //     const toTree = createUrlTreeFromSnapshot(to, []);
    //     // Skip the transition if the only thing changing is the fragment and queryParams
    //     if (
    //       router.isActive(toTree, {
    //         paths: 'exact',
    //         matrixParams: 'exact',
    //         fragment: 'ignored',
    //         queryParams: 'ignored',
    //       })
    //     ) {
    //       transition.skipTransition();
    //     }
    //   },
    // }),
    // withViewTransitions(), // removed: Angular 20 logs AbortError in dev mode by design; DOM updates too slow for transition window
    withComponentInputBinding(),
    withRouterConfig({
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
    }),
    withPreloading(PreloadAllModules),
  ),
  // importProvidersFrom(ApiModule.forRoot({})),
  provideHttpClient(withInterceptorsFromDi()),
]);
